// Text and number clean-up for imported cells. Numbers come back as plain
// decimal strings so money and quantities go through core's exact integer
// parsers (parseMoney / parseQty), never through floats.

import { roundDecimal, toAsciiDigits } from '@ims/core'

export { roundDecimal, toAsciiDigits }

/** Lowercase, ASCII digits, punctuation collapsed to single spaces. */
export function normalizeHeader(value: string): string {
  return toAsciiDigits(value)
    .toLowerCase()
    .replace(/[^a-z0-9%\p{Script=Devanagari}]+/gu, ' ')
    .trim()
}

export class CellError extends Error {}

/**
 * "Rs. 1,00,000.50/-" → "100000.50", "1.234,56" → "1234.56", "(12)" → "-12".
 * Empty → null. Anything else that isn't a number throws CellError.
 */
export function cleanNumber(raw: string | null | undefined): string | null {
  if (raw == null) return null
  let s = toAsciiDigits(String(raw)).trim()
  if (s === '' || s === '-') return null
  let negative = false
  if (/^\(.*\)$/.test(s)) {
    negative = true
    s = s.slice(1, -1)
  }
  s = s
    .replace(/^(rs\.?|nrs\.?|npr|inr|रु\.?|रू\.?|₹|\$)\s*/i, '')
    .replace(/\s*(\/-|rs\.?|npr|रु\.?)$/i, '')
    .replace(/[\s\u00a0\u202f']/g, '')
  if (s.startsWith('-')) {
    negative = !negative
    s = s.slice(1)
  }
  const hasDot = s.includes('.')
  const hasComma = s.includes(',')
  if (hasDot && hasComma) {
    // Whichever comes last is the decimal mark.
    s =
      s.lastIndexOf(',') > s.lastIndexOf('.')
        ? s.replace(/\./g, '').replace(',', '.')
        : s.replace(/,/g, '')
  } else if (hasComma) {
    const commas = s.split(',').length - 1
    const decimals = s.length - s.lastIndexOf(',') - 1
    // "12,5" / "12,50" are decimal commas; "1,000" and "1,00,000" are grouping.
    s = commas === 1 && decimals > 0 && decimals <= 2 ? s.replace(',', '.') : s.replace(/,/g, '')
  }
  if (!/^\d*\.?\d*$/.test(s) || !/\d/.test(s)) {
    throw new CellError(`“${raw}” is not a number`)
  }
  if (s.startsWith('.')) s = `0${s}`
  if (s.endsWith('.')) s = s.slice(0, -1)
  return negative ? `-${s}` : s
}

/**
 * A date cell as YYYY-MM-DD: "2027-03-31", "31/03/2027", "31-3-27", "3/31/2027".
 * Day-first unless the numbers only make sense month-first. Invalid → CellError.
 */
export function parseDateCell(raw: string): string {
  const s = toAsciiDigits(raw).trim()
  let y: number
  let m: number
  let d: number
  const iso = /^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})$/.exec(s)
  const dmy = /^(\d{1,2})[-/.](\d{1,2})[-/.](\d{2}|\d{4})$/.exec(s)
  if (iso) {
    y = Number(iso[1])
    m = Number(iso[2])
    d = Number(iso[3])
  } else if (dmy) {
    const a = Number(dmy[1])
    const b = Number(dmy[2])
    y = Number(dmy[3]) + (dmy[3]!.length === 2 ? 2000 : 0)
    // Day first (as in Nepal), unless the second number can only be a day.
    ;[d, m] = b > 12 && a <= 12 ? [b, a] : [a, b]
  } else {
    throw new CellError(`“${raw}” is not a date (use YYYY-MM-DD or DD/MM/YYYY)`)
  }
  const date = new Date(Date.UTC(y, m - 1, d))
  if (date.getUTCFullYear() !== y || date.getUTCMonth() !== m - 1 || date.getUTCDate() !== d) {
    throw new CellError(`“${raw}” is not a real date`)
  }
  return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`
}

const TRUE_WORDS = new Set(['yes', 'y', 'true', '1', 'incl', 'inclusive', 'हो', 'छ'])
const FALSE_WORDS = new Set(['no', 'n', 'false', '0', 'excl', 'exclusive', 'होइन', 'छैन'])

/** "Yes" / "no" / "हो" → boolean; empty → null; anything else throws CellError. */
export function parseBool(raw: string | null | undefined): boolean | null {
  const s = (raw ?? '').trim().toLowerCase()
  if (s === '') return null
  if (TRUE_WORDS.has(s)) return true
  if (FALSE_WORDS.has(s)) return false
  throw new CellError(`“${raw}” should be Yes or No`)
}

/**
 * Cells copied from Excel or Google Sheets: tab-separated lines, with cells
 * that contain tabs, newlines or quotes wrapped in double quotes.
 */
export function parseTsv(text: string): string[][] {
  const rows: string[][] = []
  let row: string[] = []
  let cell = ''
  let quoted = false
  for (let i = 0; i < text.length; i++) {
    const ch = text[i]
    if (quoted) {
      if (ch === '"' && text[i + 1] === '"') {
        cell += '"'
        i++
      } else if (ch === '"') {
        quoted = false
      } else {
        cell += ch
      }
    } else if (ch === '"' && cell === '') {
      quoted = true
    } else if (ch === '\t') {
      row.push(cell)
      cell = ''
    } else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && text[i + 1] === '\n') i++
      row.push(cell)
      rows.push(row)
      row = []
      cell = ''
    } else {
      cell += ch
    }
  }
  if (cell !== '' || row.length > 0) {
    row.push(cell)
    rows.push(row)
  }
  return rows
}

/** Trims cells and drops fully empty trailing rows. */
export function tidyRows(rows: string[][]): string[][] {
  const out = rows.map((r) => r.map((c) => (c ?? '').toString().trim()))
  while (out.length > 0 && out[out.length - 1]!.every((c) => c === '')) out.pop()
  return out
}
